import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/internal/Observable';
import { of } from 'rxjs';
import { catchError, filter, map, share, take } from 'rxjs/operators';
import { isEmpty, isUndefined } from 'lodash';
import { ILocalizationData, LOCALIZATION_KEYS } from './localization.data';
import {
  CookieService,
  defaultAppLocaleLanguage,
  ILocaleLanguage,
  supportedAppLocalesLanguages,
} from 'i2g-ng-utils';

@Injectable()
export class LocalizationService {
  constructor(
    private _translateService: TranslateService,
    private _cookieService: CookieService
  ) {
    this._initializeLocale();
  }

  isLocalizationsReady(): Observable<boolean> {
    if (isEmpty(this.getInstantTranslations(this.getCurrentLocale()))) {
      return this._translateService.onLangChange.pipe(
        filter(
          (langChangeEvent: LangChangeEvent) =>
            langChangeEvent.lang === this.getCurrentLocale()
        ),
        map(
          (langChangeEvent: LangChangeEvent) =>
            !isUndefined(langChangeEvent.translations)
        ),
        take(1)
      );
    } else {
      return of(true);
    }
  }

  getSupportedCountry(country: string = ''): string {
    const browserCultureLang = this._translateService.getBrowserCultureLang();
    const supportedLocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguage) =>
          localeLanguage.locale.split('-')[1] === country.toUpperCase()
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale === browserCultureLang
      ) ||
      defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale.split('-')[1];
  }

  /**
   * Returns the best supported locale based on the following priorities:
   *  - case insensitive match
   *  - browser culture language match, e.g. "de-DE"
   *  - browser language match, e.g. "de"
   *  - if not found, fallback to default locale: "en-US"
   */
  getSupportedLocale(locale: string = ''): string {
    const browserCultureLang = this._translateService.getBrowserCultureLang();
    const browserLang = this._translateService.getBrowserLang();

    const supportedLocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguage) =>
          localeLanguage.locale.toLowerCase() === locale.toLowerCase()
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale === browserCultureLang
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale.split('-')[0] === browserLang
      ) ||
      defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale;
  }

  getInstantTranslations(lang: string): any {
    return this._translateService.translations[lang];
  }

  getTranslations(
    lang: string
  ): any | Observable<{ lang: string; translations: any }> {
    return this._translateService.getTranslation(lang).pipe(
      map((translations) => ({ lang, translations })),
      catchError(() => {
        lang = lang.split('-')[0];

        return this._translateService.getTranslation(lang).pipe(
          map((translations) => ({ lang, translations })),
          catchError(() => {
            const defaultLang = this._translateService.getDefaultLang();
            const translations = this.getInstantTranslations(defaultLang);

            if (translations) {
              return of({ lang: defaultLang, translations });
            } else {
              return this._translateService
                .getTranslation(defaultLang)
                .pipe(
                  map((translations) => ({ lang: defaultLang, translations }))
                );
            }
          })
        );
      }),
      share()
    );
  }

  getCurrentLocale() {
    return this._translateService.currentLang;
  }

  getFormattedLocale(locale: string = 'en_US') {
    locale = locale.replace('_', '-');

    const supportedLocaleLanguage: ILocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguageInfo: ILocaleLanguage) =>
          localeLanguageInfo.locale === locale
      ) || defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale;
  }

  setDefaultLanguage() {
    this._translateService.setDefaultLang(defaultAppLocaleLanguage.locale);
  }

  setLocale(locale: string) {
    if (this._translateService.currentLang !== locale) {
      if (this._translateService.getDefaultLang() !== locale) {
        this.getTranslations(locale)
          .pipe(take(1))
          .subscribe(({ lang }) => {
            this._translateService.use(lang);
          });
      } else {
        this._translateService.use(locale);
      }
    }
  }

  getStoredCountry(): string {
    return this._cookieService.getCookie(LOCALIZATION_KEYS.COUNTRY);
  }

  isSupportedLocale(locale: string) {
    return supportedAppLocalesLanguages.some(
      (localeLanguage) => localeLanguage.locale === locale
    );
  }

  isSupportedCountry(country: string) {
    return supportedAppLocalesLanguages.some(
      (localeLanguage) => localeLanguage.locale.split('-')[1] === country
    );
  }

  getLocalizationData(): ILocalizationData {
    return {
      country:
        this._cookieService.getCookie(LOCALIZATION_KEYS.COUNTRY) ||
        this.getSupportedCountry(),
      defaultLanguage: this._translateService.getDefaultLang(),
      currentLocale: this.getCurrentLocale() || this.getSupportedLocale(),
      supportedLanguages: supportedAppLocalesLanguages,
    };
  }

  private _initializeLocale() {
    this.setDefaultLanguage();
    const locale = this._cookieService
      .getCookie(LOCALIZATION_KEYS.LOCALE)
      .replace('_', '-');
    if (locale && this.isSupportedLocale(locale)) {
      this._translateService.use(locale);
    } else {
      this.setLocale(this.getSupportedLocale());
    }
  }
}
